<template>
	<div v-if="showCombinedOptionSelector" class="product__selector">
		<div class="product__selector-label">
			{{ colorOptionLabel }}:
		</div>
		<div class="product__swatches">
			<button
				v-for="item in colorSelectorItems"
				:key="item.value"
				type="button"
				class="product__swatch"
				:class="{ 'product__swatch--active': item.value === selectedColorValue, 'is-disabled': item.isDisabled }"
				:style="{ backgroundColor: item.color }"
				@click="selectColorOption(item.value)"
				:aria-label="item.value"
				:aria-disabled="item.isDisabled"
			></button>
		</div>

		<div class="product__selector-label">
			{{ secondaryOptionLabel }}:
		</div>
		<div class="product__selector-row">
			<button
				v-for="item in secondarySelectorItems"
				:key="item.value"
				class="product__selector-btn"
				:class="{ 'product__selector-btn--active': item.value === selectedSecondaryValue, 'is-disabled': item.isDisabled }"
				@click.prevent="selectSecondaryOption(item.value)"
				:aria-disabled="item.isDisabled"
			>
				{{ item.value }}
			</button>
		</div>
	</div>

	<div v-if="showDeviceSelector" class="product__selector">
		<div class="product__selector-label">
			{{ isUkLocale ? 'Інші кольори з лінійки продукції' : 'Другие цвета из линейки продукции' }}
		</div>
		<div class="product__swatches">
			<button
				v-for="variant in variants"
				:key="variant.id"
				type="button"
				class="product__swatch"
				:class="{ 'product__swatch--active': String(variant.id) === selectedVariantId, 'is-disabled': !variant.available }"
				:style="{ backgroundColor: getVariantColor(variant) }"
				@click="selectVariant(variant.id)"
				:aria-label="variant.title"
				:aria-disabled="!variant.available"
			></button>
		</div>
	</div>

	<div v-if="showNonDeviceSelector && isNotForSell == false" class="product__selector">
		<div class="product__selector-label">
			{{ isUkLocale ? 'Варіант' : 'Вариант' }}:
		</div>
		<div class="product__selector-row">
			<button
				v-for="variant in variants"
				:key="variant.id"
				class="product__selector-btn"
				:class="{ 'product__selector-btn--active': String(variant.id) === selectedVariantId, 'is-disabled': !variant.available }"
				@click.prevent="selectVariant(variant.id)"
				:aria-disabled="!variant.available"
			>
				{{ variant.title }}
			</button>
		</div>
	</div>

	<div v-if="showSticksSelector" class="product__selector">
		<div class="product__selector-label">
			{{ isUkLocale ? 'Кількість' : 'Количество' }}:
		</div>
		<div class="product__selector-row">
			<template v-for="item in sticksSelectorItems" :key="item.id">
				<button
					v-if="item.isCurrent"
					type="button"
					class="product__selector-btn product__selector-btn--active"
				>
					{{ item.label }}
				</button>
				<a
					v-else
					:href="item.url"
					class="product__selector-btn"
				>
					{{ item.label }}
				</a>
			</template>
		</div>
	</div>

	<div class="product__purchase" v-if="isNotForSell == false">
		<div class="product__purchase-row">
			<div class="product__price h6" v-if="!isServiceClosedProduct">
				<span v-if="productData.price_xp" class="product__price-xp">{{productData.price_xp}} XP</span>
				<span v-if="productData.price_xp && currentPrice > 0" class="product__price-small">+</span>
				<span
					:class="{ 'product__price-small': productData.price_xp }"
					v-if="currentPrice > 0"
				>{{ formatMoney(currentPrice) }}</span>

				<span
					v-if="productData.specials?.isTestdrive"
					class="product__price-note"
				>
<!--					на 21 день-->
				</span>
			</div>

			<div :class="['product__add-btn', { 'product__add-btn--error': isAddBlocked }]">
				<Button
					:label-uk="addToCartLabelUk"
					:label-ru="addToCartLabelRu"
					variant="orange"
					:disabled="isLoading || isBuyButtonDisabled"
					:aria-disabled="isAddBlocked"
					:aria-describedby="isAddBlocked ? 'add-btn-popover' : null"
					:class-name="{'btn--loading': isLoading, 'is-disabled': isBuyButtonDisabled || isAddBlocked }"
					@click="addToCart"
				/>
				<span
					v-if="isAddBlocked"
					id="add-btn-popover"
					class="popover"
				>
					{{ addBlockedMessage }}
				</span>
			</div>

			<div v-if="showQtyControls" class="product__qty">
				<div class="qty">
					<div class="qty__row">
						<button
							class="qty__btn qty__btn--minus"
							type="button"
							@click="decreaseQty"
							:aria-label="isUkLocale ? 'Зменшити кількість' : 'Уменьшить количество'"
						>
							<IconMinus />
						</button>
						<input
							type="text"
							class="qty__input"
							:value="quantity"
							min="1"
							:max="quantityLimit"
							@input="onQtyInput"
							:aria-label="isUkLocale ? 'Кількість' : 'Количество'"
						>
						<button
							class="qty__btn qty__btn--plus"
							:class="isQtyPlusBlocked ? 'qty__btn--blocked' : ''"
							type="button"
							:aria-disabled="isQtyPlusBlocked"
							:aria-describedby="isQtyPlusBlocked ? 'qty-plus-popover' : null"
							@click="increaseQty"
							:aria-label="isUkLocale ? 'Збільшити кількість' : 'Увеличить количество'"
						>
							<IconPlus />
							<span
								v-if="isQtyPlusBlocked"
								id="qty-plus-popover"
								class="popover"
							>
								{{ addBlockedMessage }}
							</span>
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>

	<Teleport to=".buy-bar__aside">
		<div :class="['product__add-btn', { 'product__add-btn--error': isAddBlocked }]">
			<Button
				:label-uk="addToCartLabelUk"
				:label-ru="addToCartLabelRu"
				variant="orange"
				:disabled="isLoading || isBuyButtonDisabled"
				:aria-disabled="isAddBlocked"
				:aria-describedby="isAddBlocked ? 'buy-bar-add-btn-popover' : null"
				:class-name="{'btn--loading': isLoading, 'is-disabled': isBuyButtonDisabled || isAddBlocked }"
				@click="addToCart"
			/>
			<span
				v-if="isAddBlocked"
				id="buy-bar-add-btn-popover"
				class="popover"
			>
				{{ addBlockedMessage }}
			</span>
		</div>
	</Teleport>

	<Teleport to="[data-vue-buy-bar-price]">
		<span v-if="!isServiceClosedProduct">{{ formatMoney(currentPrice) }}</span>
	</Teleport>

	<Teleport to="[data-vue-product-title-color]">
		<span v-if="showTitleColorName"><br>{{ selectedVariantColorName }}</span>
	</Teleport>
	<Teleport to="[data-vue-product-title-color-mobile]">
		<span v-if="showTitleColorName"><br>{{ selectedVariantColorName }}</span>
	</Teleport>

	<Teleport to=".buy-bar__taste" v-if="showDeviceLabel && selectedVariant">
		<div
			class="buy-bar__capsule"
			:style="{ backgroundColor: getVariantColor(selectedVariant) }"
		></div>
		<div class="buy-bar__taste-label">{{ selectedVariant.title }}</div>
	</Teleport>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { CART_EVENTS, createCartManager } from '../cart-manager/cartManager.js';
import XpBalance from '@/js/xp-balance';
import { animateImage } from '@/js/utils/animateImage.js'
import Button from './Button.vue';
import IconMinus from '@images/i-qty-minus.svg';
import IconPlus from '@images/i-qty-plus.svg';

const props = defineProps({
	initialVariantId: {
		type: [String, Number],
		default: ''
	}
});

const cartManager = createCartManager();
const xpBalanceService = new XpBalance();
let managerInitPromise = null;
let stopAfterChangeSubscription = null;

const quantity = ref(1);
const selectedVariantId = ref('');
const isLoading = ref(false);
const cart = ref(null);
const xpBalance = ref(null);

const productData = computed(() => window.productData || {});

const variants = computed(() => {
	const raw = productData.value?.variants;

	if (Array.isArray(raw)) {
		return raw.filter((variant) => variant && variant.id);
	}

	if (raw && typeof raw === 'object') {
		if (raw.id) {
			return [raw];
		}

		return Object.values(raw).filter((variant) => variant && variant.id);
	}

	return [];
});

const parseOptionsArray = (raw) => {
	if (Array.isArray(raw)) {
		return raw.map((item) => String(item || '').trim());
	}

	if (typeof raw === 'string') {
		const trimmed = raw.trim();

		if (!trimmed) {
			return [];
		}

		try {
			const parsed = JSON.parse(trimmed);
			if (Array.isArray(parsed)) {
				return parsed.map((item) => String(item || '').trim());
			}
		} catch (error) {
			return [];
		}
	}

	if (raw && typeof raw === 'object') {
		return Object.values(raw).map((item) => String(item || '').trim());
	}

	return [];
};

const productOptionNames = computed(() => parseOptionsArray(productData.value?.options));

const normalizedVariants = computed(() => {
	return variants.value.map((variant) => ({
		...variant,
		parsedOptions: parseOptionsArray(variant?.options)
	}));
});

const COLOR_OPTION_PATTERN = /color|colour|колір|цвет/i;

const colorOptionIndex = computed(() => {
	return productOptionNames.value.findIndex((name) => COLOR_OPTION_PATTERN.test(String(name || '')));
});

const secondaryOptionIndex = computed(() => {
	if (productOptionNames.value.length <= 1) {
		return -1;
	}

	for (let index = 0; index < productOptionNames.value.length; index += 1) {
		if (index !== colorOptionIndex.value) {
			return index;
		}
	}

	return -1;
});

const selectedColorValue = ref('');
const selectedSecondaryValue = ref('');

const getVariantOptionValue = (variant, optionIndex) => {
	if (optionIndex < 0) {
		return '';
	}

	const options = Array.isArray(variant?.parsedOptions)
		? variant.parsedOptions
		: parseOptionsArray(variant?.options);

	return String(options?.[optionIndex] || '').trim();
};

const quantityLimit = computed(() => {
	if (productData.value?.specials?.isUpgrade || productData.value?.specials?.isTestdrive) {
		return 1;
	}

	return productData.value?.specials?.isDevice ? 5 : 50;
});

const showQtyControls = computed(() => {
	return !productData.value?.specials?.isUpgrade &&
				 !productData.value?.specials?.isTestdrive &&
				 productData.value.available;
});

const showDeviceLabel = computed(() => {
	return !productData.value?.specials?.isUpgrade &&
				 !productData.value?.specials?.isTestdrive &&
				 productData.value?.specials?.isDevice;
});



const selectedVariantCartQty = computed(() => {
	const lineItems = Array.isArray(cart.value?.items) ? cart.value.items : [];
	const currentVariantId = Number(selectedVariantId.value);

	if (!currentVariantId) {
		return 0;
	}

	return lineItems
		.filter((item) => Number(item?.variant_id) === currentVariantId)
		.reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0);
});

const remainingQty = computed(() => {
	return Math.max(quantityLimit.value - selectedVariantCartQty.value, 0);
});

const isUpgradeProduct = computed(() => Boolean(productData.value?.specials?.isUpgrade));
const isTestdriveProduct = computed(() => Boolean(productData.value?.specials?.isTestdrive));

const isUpgradeBlockedInCart = computed(() => {
	if (!cart.value) {
		return false;
	}

	const hasTopLevelBlock = Boolean(
		cart.value?.blocked?.isUpgrade ||
		cart.value?.blocked_upgrade ||
		cart.value?.blockers?.upgrade
	);

	if (hasTopLevelBlock) {
		return true;
	}

	const lineItems = Array.isArray(cart.value?.items) ? cart.value.items : [];
	return lineItems.some((item) => Boolean(item?.blocked?.isUpgrade));
});

const isTestdriveBlockedInCart = computed(() => {
	if (!cart.value) {
		return false;
	}

	const hasTopLevelBlock = Boolean(
		cart.value?.blocked?.isTestdrive ||
		cart.value?.blockers?.testdrive ||
		(Number(cart.value?.test_drive_qty) || 0) > 0
	);

	if (hasTopLevelBlock) {
		return true;
	}

	const lineItems = Array.isArray(cart.value?.items) ? cart.value.items : [];
	return lineItems.some((item) => Boolean(item?.specials?.isTestdrive));
});

const isUpgradeAddBlocked = computed(() => isUpgradeProduct.value && isUpgradeBlockedInCart.value);
const isTestdriveAddBlocked = computed(() => isTestdriveProduct.value && isTestdriveBlockedInCart.value);

const isXpProduct = computed(() => Boolean(productData.value?.specials?.isXP || Number(productData.value?.price_xp) > 0));

const xpPricePerItem = computed(() => {
	const raw = Number(productData.value?.price_xp);
	if (!Number.isFinite(raw) || raw <= 0) {
		return 0;
	}

	return raw;
});

const quantityToAdd = computed(() => {
	return showQtyControls.value ? quantity.value : 1;
});

const requestedXpTotal = computed(() => {
	return xpPricePerItem.value * quantityToAdd.value;
});

const cartTotalXp = computed(() => {
	const total = Number(cart.value?.total_xp);
	return Number.isFinite(total) ? total : 0;
});

const currentBalance = computed(() => {
	const balance = Number(xpBalance.value);
	return Number.isFinite(balance) ? balance : null;
});

const isXpAuthorized = computed(() => {
	return Boolean(cart.value?.customer?.status === 'verified');
});

const availableXp = computed(() => {
	if (currentBalance.value === null) {
		return null;
	}

	return currentBalance.value - cartTotalXp.value;
});

const isXpAuthBlocked = computed(() => {
	if (!isXpProduct.value || xpPricePerItem.value <= 0) {
		return false;
	}

	if (!cart.value) {
		return false;
	}

	return !isXpAuthorized.value;
});

const isXpBalanceBlocked = computed(() => {
	if (!isXpProduct.value || xpPricePerItem.value <= 0) {
		return false;
	}

	if (availableXp.value === null) {
		return false;
	}

	return availableXp.value < requestedXpTotal.value;
});

const isXpAddBlocked = computed(() => {
	return isXpAuthBlocked.value || isXpBalanceBlocked.value;
});

const isQtyPlusBlocked = computed(() => {
	if (!selectedVariantId.value) {
		return true;
	}

	if (remainingQty.value <= 0) {
		return true;
	}

	return quantity.value >= remainingQty.value;
});

const isAddBlocked = computed(() => {
	if (isUpgradeAddBlocked.value) {
		return true;
	}

	if (isTestdriveAddBlocked.value) {
		return true;
	}

	if (isXpAddBlocked.value) {
		return true;
	}

	if (!selectedVariantId.value) {
		return true;
	}

	if (remainingQty.value <= 0) {
		return true;
	}

	return quantity.value > remainingQty.value;
});

const addBlockedMessage = computed(() => {
	if (isUpgradeAddBlocked.value || isTestdriveAddBlocked.value) {
		return 'Можна скористатись лише 1 раз.';
	}

	if (isXpAuthBlocked.value) {
		return isUkLocale.value
			? 'Верифікуйся, щоб отримати можливість замовити товар'
			: 'Верифицируйся, чтобы получить возможность заказать товар';
	}

	if (isXpBalanceBlocked.value) {
		return isUkLocale.value
			? 'У вас недостатньо ХР для покупки даного товару'
			: 'У вас недостаточно XP для покупки данного товара';
	}

	const productLabel = productData.value?.specials?.isDevice ? 'пристроїв' : 'пачок стіків';
	if (productData.value?.specials?.isDevice) {
		return isUkLocale.value
			? `Ти можеш придбати за один раз не більше ${quantityLimit.value} пристроїв`
			: `Ты можешь купить за один раз не более ${quantityLimit.value} устройств`;
	}

	if (productData.value?.specials?.isSticks) {
		return isUkLocale.value
			? `Ти можеш придбати за один раз не більше ${quantityLimit.value} пачок стіків`
			: `Ты можешь купить за один раз не более ${quantityLimit.value} пачек стиков`;
	}

	return `Ти можеш придбати за один раз не більше ${quantityLimit.value} ${productLabel}`;
});

const showCombinedOptionSelector = computed(() => {
	return variants.value.length > 1 &&
		productOptionNames.value.length > 1 &&
		colorOptionIndex.value >= 0 &&
		secondaryOptionIndex.value >= 0;
});

const locale = computed(() => {
	return window.Shopify.locale;
});

const isUkLocale = computed(() => locale.value.startsWith('uk'));

const normalizeOptionName = (value) => String(value || '').trim().toLowerCase();

const localizeOptionName = (name) => {
	const normalized = normalizeOptionName(name);

	const labelsUk = {
		size: 'Розмір',
		'розмір': 'Розмір',
		color: 'Колір',
		colour: 'Колір',
		'колір': 'Колір',
		'цвет': 'Колір'
	};

	const labelsRu = {
		size: 'Размер',
		'розмір': 'Размер',
		color: 'Цвет',
		colour: 'Цвет',
		'колір': 'Цвет',
		'цвет': 'Цвет'
	};

	const source = isUkLocale.value ? labelsUk : labelsRu;
	return source[normalized] || String(name || '').trim() || (isUkLocale.value ? 'Варіант' : 'Вариант');
};

const showDeviceSelector = computed(() => {
	return productData.value?.type === 'device' &&
		variants.value.length > 1 &&
		!showCombinedOptionSelector.value;
});

const colorOptionLabel = computed(() => {
	const optionName = productOptionNames.value[colorOptionIndex.value];
	if (!optionName) {
		return isUkLocale.value ? 'Колір' : 'Цвет';
	}

	return localizeOptionName(optionName);
});

const secondaryOptionLabel = computed(() => {
	const optionName = productOptionNames.value[secondaryOptionIndex.value];
	if (!optionName) {
		return isUkLocale.value ? 'Варіант' : 'Вариант';
	}

	return localizeOptionName(optionName);
});

const showNonDeviceSelector = computed(() => {
	return productData.value?.type !== 'device' && !showSticksSelector.value && variants.value.length > 1 && productOptionNames.value.length <= 1;
});

const isNotForSell = computed(()=> {
	return productData.value?.specials?.isNotForPurchase;
})

const showSticksSelector = computed(() => {
	return productData.value?.specials?.isSticks || productData.value?.type === 'sticks';
});

const STICKS_PACK_LIMIT = 400;

const sticksGroup = computed(() => {
	const raw = productData.value?.sticks_group;

	if (Array.isArray(raw)) {
		return raw.filter((item) => item && item.id);
	}

	if (raw && typeof raw === 'object') {
		if (raw.id) {
			return [raw];
		}

		return Object.values(raw).filter((item) => item && item.id);
	}

	return [];
});

const normalizeSticksPrice = (value) => {
	return Number(value || 0) / 100;
};

const parsePacksCountFromTitle = (title) => {
	const normalizedTitle = String(title || '');
	const match = normalizedTitle.match(/\b\d{1,2}\b/);
	return match ? parseInt(match[0], 10) : null;
};

const sticksSelectorItems = computed(() => {
	return sticksGroup.value
		.map((item) => {
			const normalizedPrice = normalizeSticksPrice(item?.price);
			const itemTitle = item?.title || item?.product_title || item?.variant_title || item?.name || productData.value?.title || '';
			const packsCount = parsePacksCountFromTitle(itemTitle);
			
			const isBlock = normalizedPrice > STICKS_PACK_LIMIT;
			const blockPacks = packsCount && packsCount > 1 ? packsCount : 10;
			let label = isUkLocale.value ? '1 пачка (20 стіків)' : '1 пачка (20 стиков)';

			if (isBlock) {
				label = isUkLocale.value
					? `Блок з ${blockPacks} пачками (${blockPacks * 20} стіків)`
					: `Блок из ${blockPacks} пачек (${blockPacks * 20} стиков)`;
			}

			return {
				id: String(item?.id || ''),
				url: String(item?.url || '#'),
				isCurrent: Boolean(item?.is_current),
				normalizedPrice,
				label: label
			};
		})
		.sort((left, right) => left.normalizedPrice - right.normalizedPrice);
});

const readColorValue = (value) => {
	if (typeof value === 'string') {
		return value.trim();
	}

	if (value && typeof value === 'object') {
		if (typeof value.value === 'string') {
			return value.value.trim();
		}

		if (typeof value.hex === 'string') {
			return value.hex.trim();
		}
	}

	return '';
};

const normalizeColor = (value) => {
	const color = readColorValue(value);
	if (!color) {
		return '#000000';
	}

	if (window.CSS && typeof window.CSS.supports === 'function') {
		return window.CSS.supports('color', color) ? color : '#000000';
	}

	return color;
};

const getVariantColor = (variant) => {
	return normalizeColor(variant?.color_hex);
};

const matchesCurrentSecondaryOption = (variant, colorValue) => {
	if (!selectedSecondaryValue.value) {
		return true;
	}

	return getVariantOptionValue(variant, secondaryOptionIndex.value) === selectedSecondaryValue.value &&
		getVariantOptionValue(variant, colorOptionIndex.value) === colorValue;
};

const matchesCurrentColorOption = (variant, secondaryValue) => {
	if (!selectedColorValue.value) {
		return true;
	}

	return getVariantOptionValue(variant, colorOptionIndex.value) === selectedColorValue.value &&
		getVariantOptionValue(variant, secondaryOptionIndex.value) === secondaryValue;
};

const colorSelectorItems = computed(() => {
	if (!showCombinedOptionSelector.value) {
		return [];
	}

	const map = new Map();

	for (const variant of normalizedVariants.value) {
		const colorValue = getVariantOptionValue(variant, colorOptionIndex.value);
		if (!colorValue) {
			continue;
		}

		if (!map.has(colorValue)) {
			map.set(colorValue, {
				value: colorValue,
				colorHex: '',
				hasAvailableForCurrentSecondary: false
			});
		}

		const entry = map.get(colorValue);
		const colorHex = readColorValue(variant?.color_hex);

		if (!entry.colorHex && colorHex) {
			entry.colorHex = colorHex;
		}

		if (variant.available && matchesCurrentSecondaryOption(variant, colorValue)) {
			entry.hasAvailableForCurrentSecondary = true;
		}
	}

	return Array.from(map.values()).map((item) => ({
		value: item.value,
		color: normalizeColor(item.colorHex),
		isDisabled: !item.hasAvailableForCurrentSecondary
	}));
});

const secondarySelectorItems = computed(() => {
	if (!showCombinedOptionSelector.value) {
		return [];
	}

	const map = new Map();

	for (const variant of normalizedVariants.value) {
		const secondaryValue = getVariantOptionValue(variant, secondaryOptionIndex.value);
		if (!secondaryValue) {
			continue;
		}

		if (!map.has(secondaryValue)) {
			map.set(secondaryValue, {
				value: secondaryValue,
				hasAvailableForCurrentColor: false
			});
		}

		const entry = map.get(secondaryValue);
		if (variant.available && matchesCurrentColorOption(variant, secondaryValue)) {
			entry.hasAvailableForCurrentColor = true;
		}
	}

	return Array.from(map.values()).map((item) => ({
		value: item.value,
		isDisabled: !item.hasAvailableForCurrentColor
	}));
});

const findMatchingVariant = ({ colorValue = '', secondaryValue = '' }) => {
	const matches = normalizedVariants.value.filter((variant) => {
		const variantColor = getVariantOptionValue(variant, colorOptionIndex.value);
		const variantSecondary = getVariantOptionValue(variant, secondaryOptionIndex.value);

		if (colorValue && variantColor !== colorValue) {
			return false;
		}

		if (secondaryValue && variantSecondary !== secondaryValue) {
			return false;
		}

		return true;
	});

	if (!matches.length) {
		return null;
	}

	return matches.find((variant) => variant.available) || matches[0];
};

const selectColorOption = (colorValue) => {
	if (!showCombinedOptionSelector.value || !colorValue) {
		return;
	}

	const exact = findMatchingVariant({
		colorValue,
		secondaryValue: selectedSecondaryValue.value
	});

	const fallback = exact || findMatchingVariant({ colorValue });
	if (!fallback?.id) {
		return;
	}

	selectedVariantId.value = String(fallback.id);
};

const selectSecondaryOption = (secondaryValue) => {
	if (!showCombinedOptionSelector.value || !secondaryValue) {
		return;
	}

	const exact = findMatchingVariant({
		colorValue: selectedColorValue.value,
		secondaryValue
	});

	const fallback = exact || findMatchingVariant({ secondaryValue });
	if (!fallback?.id) {
		return;
	}

	selectedVariantId.value = String(fallback.id);
};

const selectedVariant = computed(() => {
	return normalizedVariants.value.find((variant) => String(variant.id) === String(selectedVariantId.value)) || null;
});

const isDelistedProduct = computed(() => Boolean(productData.value?.specials?.isDelisted));
const isServiceClosedProduct = computed(() => Boolean(productData.value?.specials?.isServiceClosed));

const isSelectedVariantUnavailable = computed(() => {
	if (!selectedVariantId.value) {
		return false;
	}

	return !selectedVariant.value?.available;
});

const isBuyButtonDisabled = computed(()=> {
	return !productData.value.available || isDelistedProduct.value || isServiceClosedProduct.value || isSelectedVariantUnavailable.value;
})

const selectedVariantColorName = computed(() => {
	const title = String(selectedVariant.value?.title || '').trim();
	if (!title || title.toLowerCase() === 'default title') {
		return '';
	}

	return title;
});

const showTitleColorName = computed(() => {
	return productData.value?.type === 'device' && !!selectedVariantColorName.value;
});

const currentPrice = computed(() => {
	return selectedVariant.value?.price || productData.value?.price || 0;
});

const addToCartLabelUk = computed(() => {
	if (isServiceClosedProduct.value) {
		return 'Сервіс закрито';
	}

	if (isDelistedProduct.value) {
		return 'Знято з продажу';
	}

	return productData.value.available && !isSelectedVariantUnavailable.value ? 'Додати до кошика' : 'Очікується';
});

const addToCartLabelRu = computed(() => {
	if (isServiceClosedProduct.value) {
		return 'Сервис закрыт';
	}

	if (isDelistedProduct.value) {
		return 'Снято с продаж';
	}

	return productData.value.available && !isSelectedVariantUnavailable.value ? 'Добавить в корзину' : 'Ожидается';
});

const formatMoney = (valueInCents = 0) => {
	const amount = Number(valueInCents || 0) / 100;
	return `${amount.toLocaleString('uk-UA').replace(',', '.')} грн`;
};

const clampQuantity = (value) => {
	const parsed = Number(value);
	if (!Number.isFinite(parsed)) {
		return 1;
	}

	const upperBound = remainingQty.value > 0 ? remainingQty.value : 1;
	return Math.min(Math.max(Math.trunc(parsed), 1), upperBound);
};

const ensureManagerReady = async () => {
	if (!managerInitPromise) {
		managerInitPromise = cartManager.init();
	}

	await managerInitPromise;
};

const refreshXpBalance = async () => {
	if (!isXpProduct.value || xpPricePerItem.value <= 0) {
		xpBalance.value = null;
		return;
	}

	xpBalance.value = await xpBalanceService.getBalance();
};

const selectVariant = (variantId) => {
	selectedVariantId.value = String(variantId);
};

const switchGalleryToVariant = (variantId) => {
  const id = String(variantId).trim();
  if (!id) return;

  const slider = document.querySelector('.product__gallery');
  const swiper = slider?.swiper;
  if (!swiper) return;

  const slides = slider.querySelectorAll('.swiper-slide');

  const index = Array.from(slides).findIndex((slide) => {
    if (slide.dataset.variantId === id) return true;

    const variants = slide.dataset.variants;
    return variants?.split(',').map(v => v.trim()).includes(id);
  });

  if (index !== -1) swiper.slideTo(index);
};

const decreaseQty = () => {
	quantity.value = clampQuantity(quantity.value - 1);
};

const increaseQty = () => {
	if (isQtyPlusBlocked.value) return;
	quantity.value = clampQuantity(quantity.value + 1);
};

const onQtyInput = (event) => {
	quantity.value = clampQuantity(event?.target?.value);
};

const resolveInitialVariantId = () => {
	if (props.initialVariantId) {
		return String(props.initialVariantId);
	}

	const firstAvailable = variants.value.find((variant) => variant.available);
	if (firstAvailable?.id) {
		return String(firstAvailable.id);
	}

	if (variants.value[0]?.id) {
		return String(variants.value[0].id);
	}

	return '';
};

const addToCart = async (event) => {
	if (isLoading.value || isAddBlocked.value || isBuyButtonDisabled.value || isDelistedProduct.value) {
		return;
	}

	const clickedButton = event?.currentTarget || event?.target?.closest?.('.btn, button, a') || null;

	isLoading.value = true;

	try {
		await ensureManagerReady();
		const qtyToAdd = showQtyControls.value ? quantity.value : 1;
		await cartManager.add([
			{
				id: Number(selectedVariantId.value),
				quantity: qtyToAdd
			}
		]);

		if (window.GTM && typeof window.GTM.addToCart === 'function') {
			window.GTM.addToCart(selectedVariantId.value, qtyToAdd);

			if (productData.value?.specials?.isXP && typeof window.GTM.addToCartXP === 'function') {
				window.GTM.addToCartXP(selectedVariantId.value, qtyToAdd);
			}
		}

		if (clickedButton) {
			try {
				animateImage(clickedButton);
				console.log('animate');
				
			} catch (animationError) {
				window.console.error(animationError);
			}
		}
	} catch (error) {
		window.console.error(error);
	} finally {
		isLoading.value = false;
	}
};

onMounted(() => {
	window.dispatchEvent(new CustomEvent('product-purchase:mounted'));
	selectedVariantId.value = resolveInitialVariantId();

	stopAfterChangeSubscription = cartManager.on(CART_EVENTS.AFTER_CHANGE, ({ cart: nextCart }) => {
		cart.value = nextCart;
		refreshXpBalance().catch(() => {});
	});

	ensureManagerReady()
		.then(() => {
			cart.value = cartManager.getState();
			refreshXpBalance().catch(() => {});
		})
		.catch(() => {});
});

watch([remainingQty, selectedVariantId], () => {
	if (!selectedVariantId.value) {
		quantity.value = 1;
		return;
	}

	if (remainingQty.value <= 0) {
		quantity.value = 1;
		return;
	}

	if (quantity.value > remainingQty.value) {
		quantity.value = remainingQty.value;
	}
});

watch(selectedVariantId, (nextVariantId, prevVariantId) => {
	const galleryEl = document.querySelector('.product__gallery');
	const isInitialMount = !prevVariantId && !!galleryEl?.dataset.firstPdpImage;
	if (!isInitialMount) switchGalleryToVariant(nextVariantId);

	if (!showCombinedOptionSelector.value) {
		selectedColorValue.value = '';
		selectedSecondaryValue.value = '';
		return;
	}

	const variant = normalizedVariants.value.find((item) => String(item.id) === String(nextVariantId));
	if (!variant) {
		return;
	}

	selectedColorValue.value = getVariantOptionValue(variant, colorOptionIndex.value);
	selectedSecondaryValue.value = getVariantOptionValue(variant, secondaryOptionIndex.value);
});

watch(showCombinedOptionSelector, (enabled) => {
	if (!enabled || !selectedVariant.value) {
		return;
	}

	selectedColorValue.value = getVariantOptionValue(selectedVariant.value, colorOptionIndex.value);
	selectedSecondaryValue.value = getVariantOptionValue(selectedVariant.value, secondaryOptionIndex.value);
});

onBeforeUnmount(() => {
	if (stopAfterChangeSubscription) {
		stopAfterChangeSubscription();
		stopAfterChangeSubscription = null;
	}
});
</script>
